import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { useUploadConflict, usePackagesActions } from "@/store/packagesSlice";

// Shown when POST /api/v1/upload/packages returns 409 because the contributor already has a
// non-terminal upload row owned by ANOTHER browser session. Two exits:
//
//   - "Take over here" → POST /sessions/{existingUploadId}/claim, plus a reserved queue slot
//     on the newly-owned upload, plus a follow-up uploadPackage dispatch that puts the user's
//     just-attempted (blocked) upload behind it. Net result: existing upload rehydrates as a
//     paused card here, and the new upload waits for it to finish before starting.
//
//   - "Not now" → closes the modal AND drops the attempted upload. User's queued files stay
//     in memory only until this dialog is dismissed; there's no automatic retry.

const statusToUserString = (status) => {
  switch (status) {
    case "PENDING_CHUNKS":
      return "waiting to start";
    case "CHUNK_UPLOAD_IN_PROGRESS":
      return "uploading";
    case "PAUSED":
      return "paused";
    case "CHUNK_UPLOAD_ASSEMBLING":
    case "READY_TO_UPLOAD_TO_PROVIDER":
    case "IN_PROGRESS_UPLOAD_TO_PROVIDER":
    case "READY_FOR_CAT_DV":
      return "finalizing on the server";
    default:
      return (status || "in progress").toLowerCase().replace(/_/g, " ");
  }
};

export const UploadConflictDialog = () => {
  const theme = useTheme();
  const { open, conflict } = useUploadConflict();
  const { claimAllVisibleUploads, closeUploadConflict } = usePackagesActions();
  // Local because the claim + queue thunk chain takes 500-1000ms and users need immediate
  // feedback that their click registered.
  const [isClaiming, setIsClaiming] = useState(false);

  const existingUploadId = conflict?.existingUploadId;
  const existingStatus = conflict?.existingStatus;
  const packageName = conflict?.existingPackageName;

  const handleTakeOver = async () => {
    if (isClaiming) return;
    // Bulk claim: makes this browser the main device for the contributor by claiming every
    // visible remote upload (not just the one the 409 named). Then the stashed pending
    // upload from the 409 is re-fired inside the same thunk and queues behind the claimed
    // rehydrations. On failure the thunk shows an alert; either way the dialog closes.
    setIsClaiming(true);
    try {
      await claimAllVisibleUploads();
    } finally {
      setIsClaiming(false);
    }
  };

  const handleClose = () => {
    if (isClaiming) return;
    closeUploadConflict();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      data-testid="upload-conflict-dialog"
      sx={{
        "& .MuiPaper-root": {
          minWidth: "320px",
          maxWidth: "480px",
          padding: "4px 2px",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, paddingBottom: "6px" }}>
        You have an active upload on another device
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, marginBottom: "10px" }}>
          Another device (or browser) is currently {statusToUserString(existingStatus)} this
          contributor&apos;s upload. Only one active upload per contributor is allowed at a
          time, so we can&apos;t start a new one here yet.
        </Typography>
        <Typography sx={{ fontSize: 14, marginBottom: "10px", fontWeight: 600 }}>
          Take it over here to continue the existing upload on this device. Your new upload
          will queue behind it and start automatically when the existing one finishes.
        </Typography>
        <Typography sx={{ fontSize: 14, marginBottom: "10px", color: theme.palette.text.secondary }}>
          Choose <strong>Not now</strong> to leave the existing upload on the other device
          and skip starting your new one.
        </Typography>
        {packageName && (
          <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
            Existing package: <strong>{packageName}</strong>
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, opacity: 0.6, marginTop: "6px" }}>
          Upload id: <code>{existingUploadId}</code>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: "10px 18px 14px", gap: "8px" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          data-testid="upload-conflict-cancel-btn"
          disabled={isClaiming}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Not now
        </Button>
        <Button
          onClick={handleTakeOver}
          variant="contained"
          data-testid="upload-conflict-take-over-btn"
          disabled={isClaiming}
          startIcon={isClaiming ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {isClaiming ? "Taking over…" : "Take over here"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadConflictDialog;
